import { Box, Dialog, DialogContent, Typography, Divider, DialogActions, Button, Stack } from "@mui/material";
import { useMemo, useState } from "react";
import TableDetalhesFechamento from "./TableDetalhesFechamento";
import FiltrosAplicadosFechamento from "./FiltrosAplicadosFechamento";
import { useMutation, useQuery, useQueryClient } from "react-query";
import axiosClient from "../../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import { desformatarFiltros, formatarFiltros } from "./helpers/formatarFiltros";
import { IDetalhesFechamentos, TFaturaFechamentoData, TFiltrosDetalheFechamento } from "../../../../../../types/api/financeiro/detalhar-fechamentos-conta-corrente";
import LoadingFechamento from "./LoadingFechamento";
import BtnExportarDados from "../../../../../../components/BtnExportarDados";
import CabecalhoFechamento from "./CabecalhoFechamento";
import { TConfiguracoesCabecalho } from "./CabecalhoFechamento/ConfiguracoesCabecalho";
import DialogEnviarFaturas from "./DialogEnviarFaturas";

export default function DialogDetalheFechamento({
    open,
    isEdit = false,
    id,
    onClose,
}: DialogDetalheFechamentoProps) {

    const queryClient = useQueryClient();

    const [filtrosParams, setFiltrosParams] = useState<TFiltrosDetalheFechamento>(defValueFiltros)
    const [selectedFaturas, setSelectedFaturas] = useState<number[]>([])
    const [openEnviarFaturas, setOpenEnviarFaturas] = useState<boolean>(false)

    //queries e mutations
    const { data, isFetching } = useQuery({
        queryKey: [`detalhar-fechamento-${id}`],
        queryFn: () => axiosClient.get(`/financeiro/conta-corrente-consumo/detalhar-fechamento/${id}`),
        onSuccess(data) {
            const params = {
                ...data.data.filtros,
                titulos_pagar_vencimento: data.data.vencimento_titulos_pagar,
                titulos_receber_vencimento: data.data.vencimento_titulos_receber,
                data_fechamento: data.data.data_fechamento,
                nome: data.data.nome,
                periodo_apuracao_inicio: data.data.apuracao_inicio,
                periodo_apuracao_fim: data.data.apuracao_fim
            }
            setFiltrosParams(desformatarFiltros(params))
        },
        cacheTime: 0,
        refetchOnWindowFocus: false
    });

    const dataDetalhesFechamentos: IDetalhesFechamentos = useMemo(() => data?.data || [], [data])

    const { mutate: attFiltros } = useMutation({
        mutationKey: 'atualizar-filtros-fechamento',
        mutationFn: (updatedFiltros: TFiltrosDetalheFechamento) => axiosClient.post(`/financeiro/conta-corrente-consumo/atualizar-filtros-fechamento/${id}`, {
            ...(formatarFiltros(updatedFiltros))
        }),
        onError: (error) => {
            console.error(error);
            enqueueSnackbar("Error ao atualizar filtros", { variant: "error" })
        },
        onSuccess: ({ data }) => {
            queryClient.invalidateQueries([`detalhar-fechamento-${id}`])
        },
    });

    //Funções manipuladoras
    const handleUpdateFiltros = (key: keyof TFiltrosDetalheFechamento, value: any) => {
        const updatedFiltros = { ...filtrosParams, [key]: value };
        setFiltrosParams(updatedFiltros);
        attFiltros(updatedFiltros);
    }

    const handleUpdateCabecalho = (value: TConfiguracoesCabecalho) => {
        const updatedFiltros = { ...filtrosParams, ...value };
        setFiltrosParams(updatedFiltros);
        attFiltros(updatedFiltros);
    }

    const handleClose = () => {
        queryClient.invalidateQueries([`pesquisar-ultimos-fechamentos`])
        queryClient.invalidateQueries([`pesquisar-proximos-fechamentos`])
        onClose()
    }

    //Valores importantes
    const faturasArray: TFaturaFechamentoData[] = useMemo(() => {
        if (dataDetalhesFechamentos.faturas && isEdit) {
            return Object.entries(dataDetalhesFechamentos.faturas)
                .map(([key, value]) => ({
                    ...value,
                    total: value.creditos - value.debitos
                })).sort((a, b) => {
                    const numA = parseInt(a.codigo_publico);
                    const numB = parseInt(b.codigo_publico);

                    return numA - numB;
                });
        }

        if (dataDetalhesFechamentos.faturas && !isEdit) {
            return Object.entries(dataDetalhesFechamentos.faturas)
                .map(([key, value]) => ({
                    ...value,
                    total: value.creditos - value.debitos
                }))
                .filter(fatura => selectedFaturas.includes(fatura.id)) // Filtrando pelos IDs selecionados
                .sort((a, b) => {
                    const numA = parseInt(a.codigo_publico);
                    const numB = parseInt(b.codigo_publico);

                    return numA - numB;
                });
        }

        return []
    }, [dataDetalhesFechamentos, isEdit, selectedFaturas])


    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="lg"
                fullWidth
                scroll="body"
            >
                {
                    isFetching &&
                    <LoadingFechamento id={id} label="Detalhando fechamento" />
                }
                {
                    !isFetching &&
                    <DialogContent>

                        <CabecalhoFechamento
                            {...{ onClose, isEdit, handleUpdateCabecalho, id, filtrosParams }}
                            data={dataDetalhesFechamentos}
                        />

                        <Divider sx={{ my: 2 }} />

                        <FiltrosAplicadosFechamento
                            {...{ isEdit, filtrosParams }}
                            handleUpdateFiltros={handleUpdateFiltros}
                        />

                        <Box display={'flex'} justifyContent={'flex-end'}>
                            <Box>
                                {!isEdit && <Typography component="div" variant="subtitle2" color="gray">Com selecionados:</Typography>}

                                <Stack direction="row" spacing={2}>
                                    <BtnExportarDados
                                        tipoExportacao={['csv', 'pdf']}
                                        disabled={faturasArray.length > 0 ? false : true}
                                        dadosArray={faturasArray}
                                        fileName={'filtros-fechamento'}
                                        cabecalhoLabels={[
                                            { label: 'Tipo público', queryKey: 'tipo_publico' },
                                            { label: 'Código público', queryKey: 'codigo_publico' },
                                            { label: 'Nome público', queryKey: 'nome' },
                                            { label: 'Crédito', queryKey: 'creditos' },
                                            { label: 'Débito', queryKey: 'debitos' },
                                            { label: 'Total', queryKey: 'total' },
                                        ]}
                                        formatos={{
                                            "Débito": "monetario",
                                            "Crédito": "monetario",
                                            "Total": "monetario"
                                        }}
                                    />
                                    {
                                        !isEdit && <Button
                                            disabled={faturasArray.length > 0 ? false : true}
                                            variant="outlined"
                                            onClick={() => setOpenEnviarFaturas(true)}
                                        >
                                            Enviar faturas
                                        </Button>
                                    }
                                </Stack>
                            </Box>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {
                            Object.entries(dataDetalhesFechamentos.faturas).length > 0 &&
                            <TableDetalhesFechamento
                                {...{ isEdit, id }}
                                detalhesFechamento={dataDetalhesFechamentos.faturas}
                                selected={selectedFaturas}
                                handleChangeSelected={(itens: number[]) => setSelectedFaturas(itens)}
                            />
                        }
                        {
                            !isFetching && Object.entries(dataDetalhesFechamentos.faturas).length === 0 &&
                            <Box display={'flex'} width={'100%'} justifyContent={'center'} mt={2} >
                                <Typography variant="body1" sx={{ opacity: 0.6 }}>Nenhum lançamento encontrado</Typography>
                            </Box>
                        }

                    </DialogContent>
                }
                <DialogActions>
                    <Button onClick={handleClose} color="primary">
                        Fechar
                    </Button>

                </DialogActions>
            </Dialog>
            {
                openEnviarFaturas &&
                <DialogEnviarFaturas
                    id={id}
                    open={openEnviarFaturas}
                    faturas={faturasArray}
                    handleClose={() => setOpenEnviarFaturas(false)}
                />
            }
        </>
    )
}

interface DialogDetalheFechamentoProps {
    open: boolean;
    isEdit?: boolean;
    id: number
    onClose: () => void;
}

const defValueFiltros: TFiltrosDetalheFechamento = {
    tipos_publicos: ['A', 'F', 'C'],
    codigos_publicos: { apenas_os_codigos: [9] },
    detalhe_publico: 'Apenas fornecedores de leite',
    periodo_apuracao: {
        inicio: '2024/01/01',
        fim: '2024/12/31'
    },
    titulo_receber_aberto: {
        funcao: null,
        tipo_documento: null,
        situacao: null,
        periodo_emissao_inicio: null,
        periodo_emissao_fim: '2024/12/26',
        periodo_vencimento_inicio: '2024/01/01',
        periodo_vencimento_fim: null,
        considera_titulos: 'Não'
    },
    titulo_pagar_aberto: {
        periodo_emissao_inicio: '2024/01/01',
        periodo_emissao_fim: '2024/11/31',
        periodo_vencimento_inicio: null,
        periodo_vencimento_fim: null,
        considera_titulos: "Não"
    },
    data_fechamento: null,
    titulos_pagar_vencimento: [],
    titulos_receber_vencimento: [],
    nome: '',
    numero_lancamento: ''
}